import { CellKeyDownEvent, ColDef, ColumnApi, GridApi, GridOptions, RowNode } from "@ag-grid-enterprise/all-modules";
import { useEffect, useMemo, useRef, useState } from "react";
import { ParseToaster } from "utils/toasters";
import { navigateToNextCell } from "../utils/utils";

const autoSizeColumns = ({ columnApi }: { api: GridApi; columnApi: ColumnApi }) => {
    const ids = columnApi.getAllColumns();
    // don't autosize last column
    if (ids) {
        ids?.pop();
        columnApi.autoSizeColumns(ids);
    }
};

export function useTemplateGridOptions(
    defaultColDef: Array<ColDef>,
    handleSelectionChange?: (o: any) => void,
    getSearchParams?: () => string | null,
    disablePagination = false
): [GridOptions, GridApi | undefined] {
    const [gridApi, setGridApi] = useState<GridApi>();

    const gridApiRef = useRef<GridApi>();

    useEffect(() => {
        if (gridApi) {
            gridApi.setColumnDefs([...defaultColDef]);
        }
    }, [gridApi]);

    useEffect(() => {
        if (gridApi) {
            console.log("registering keydown");
            type NewType = CellKeyDownEvent;

            function listener({ event, api }: NewType) {
                const keyEvent = event as KeyboardEvent;
                if (keyEvent && keyEvent.key === "a" && keyEvent.ctrlKey) {
                    api.selectAllFiltered();
                }
            }

            gridApi.addEventListener("cellKeyDown", listener);

            return () => {
                gridApi.removeEventListener("cellKeyDown", listener);
            };
        }
    }, [gridApi]);

    return [
        useMemo<GridOptions>(
            () => ({
                defaultColDef: {
                    sortable: true,
                    resizable: true,
                    editable: false,
                    menuTabs: ["filterMenuTab"],
                    filter: "agSetColumnFilter"
                },
                getRowNodeId: (data: { id: string }) => data.id,

                getRowClass: (params) => params.data.id,
                allowContextMenuWithControlKey: true,
                enableBrowserTooltips: true,
                animateRows: true,
                rowSelection: "single",
                //suppressAggFuncInHeader: true,
                onFirstDataRendered: (e) => {
                    if (getSearchParams) {
                        const encoded = getSearchParams();
                        if (encoded) {
                            try {
                                const runId = atob(encoded);
                                e.api.forEachNode((node) => {
                                    // when no valid id is provided for the node the index is used as an id and
                                    // it is a number not a string, even though the return type is
                                    // declared as string
                                    if (node.id.toString() === runId) {
                                        e.api.selectNode(node);
                                        e.api.ensureNodeVisible((node: RowNode) => node.id === runId);
                                        e.api.ensureIndexVisible(node.rowIndex);
                                    }
                                });
                            } catch (e) {
                                ParseToaster.show({
                                    message: `An error occured parsing URL! Please ensure the URL is valid.`,
                                    intent: "danger"
                                });
                            }
                        }
                    }
                    autoSizeColumns(e);
                },
                immutableData: true,
                pagination: disablePagination ? false : true,
                paginationPageSize: disablePagination ? undefined : 28,
                suppressMenuHide: true,
                //popupParent: document.getElementById("app")!,
                // getContextMenuItems: getContextMenuItems,
                // processCellForClipboard: processCellForClipboard,
                onGridReady: (e) => {
                    //sortGrid(e, "lastRuntime", "desc");
                    setGridApi(e.api);
                    gridApiRef.current = e.api;
                },
                onSelectionChanged: (e) => {
                    if (handleSelectionChange) {
                        const selectedNodes = e.api.getSelectedNodes();
                        if (selectedNodes && selectedNodes[0]) {
                            handleSelectionChange(selectedNodes[0].data);
                        }
                    }
                },
                navigateToNextCell
            }),
            []
        ),
        gridApi
    ];
}
